import { h, Host } from "@stencil/core";
import { limitSlotElements } from "../../utils/limitSlotElements";
import { getValidity } from "./validation";
/**
 * General Input component for multiple types of inputs
 * @slot (default) - custom label content
 * @slot (unit) - display a unit of measurement. Must be a ux-unit component
 * @slot (icon) - display an icon. Must be a ux-icon component
 * @slot (hint) - display a hint text that supplements the label text
 */
export class Input {
    constructor() {
        this.label = "";
        this.type = "text";
        this.placeholder = undefined;
        this.name = "";
        this.disabled = false;
        this.required = false;
        this.readonly = false;
        this.minlength = undefined;
        this.maxlength = undefined;
        this.min = undefined;
        this.max = undefined;
        this.step = undefined;
        this.value = "";
        this.spellcheck = undefined;
        this.autocapitalize = undefined;
        this.autocomplete = undefined;
        this.autofocus = false;
        this.inputmode = undefined;
        this.clearable = false;
        this.invalid = false;
        this.validity = undefined;
        this.hasFocus = false;
    }
    inputId = "input"; // id is scoped via shadow dom
    internalInput;
    hasUnit;
    host;
    internals;
    /** Emitted when a keyboard input occurred. */
    uxInput;
    /** Emitted when the value has changed . */
    uxInputChange;
    /** Emitted when the icon slot was clicked. Use e.preventDefault() to disable default behavior (focus the input) */
    uxIconClick;
    /** Emitted when the clear button was clicked. Use e.preventDefault() to disable default behavior (clear and focus input) */
    uxClear;
    /** Returns the native input element */
    async getInputElement() {
        return this.internalInput;
    }
    handleFocus = () => {
        this.hasFocus = true;
    };
    handleBlur = (event) => {
        // only emit blur if the whole component loses focus
        if (!this.host.contains(event.relatedTarget)) {
            this.hasFocus = false;
        }
    };
    getValidationMessage() {
        return this.internalInput?.validationMessage || "";
    }
    setValidity() {
        this.validate(this.invalid);
    }
    setRequired() {
        this.internals.ariaRequired = `${this.required}`;
    }
    setDisabled() {
        this.internals.ariaDisabled = `${this.disabled}`;
    }
    handleInput = async () => {
        this.value = this.internalInput.value;
        this.uxInput.emit(this.value);
        this.internals.setFormValue(this.value);
    };
    handleChange = async () => {
        this.value = this.internalInput.value;
        this.validate();
        this.uxInputChange.emit(this.value);
        this.internals.setFormValue(this.value);
    };
    handleKeyPress = (e) => {
        if (e.key === "Enter") {
            this.internals.form?.requestSubmit();
        }
    };
    handleIconClick = (e) => {
        this.uxIconClick.emit(e);
    };
    clearInput = (e) => {
        const event = this.uxClear.emit(e);
        if (!event.defaultPrevented) {
            this.value = "";
            this.internalInput.focus();
        }
    };
    validate = (customError = false) => {
        const validity = getValidity(this.internalInput, customError);
        this.validity = validity;
        this.internals.setValidity(validity, this.getValidationMessage(), this.internalInput);
    };
    updateHasUnit = () => {
        if (this.host.querySelector("ux-unit[slot='unit']")) {
            this.hasUnit = true;
        }
    };
    checkUnitSlot = () => {
        // @ts-expect-error Check for typing
        const unitSlot = this.host.shadowRoot.querySelector("slot[name='unit']");
        unitSlot.addEventListener("slotchange", this.updateHasUnit);
    };
    componentDidLoad() {
        limitSlotElements(this.host, {
            allowed: {
                icon: ["ux-icon"],
                unit: ["ux-unit"]
            },
            disallowed: {
                "": ["ux-unit"]
            }
        });
        this.checkUnitSlot();
        this.internals.form?.addEventListener("reset", () => this.validate());
        this.internals.form?.addEventListener("submit", () => this.validate());
    }
    componentWillLoad() {
        this.internals.setFormValue(this.value);
        this.setValidity();
        this.updateHasUnit();
        this.setRequired();
        this.setDisabled();
    }
    render() {
        const showClearable = !this.disabled && this.clearable && this.value.length > 0 && !this.hasUnit;
        return (h(Host, { key: '2c887f15503e66c68d26e8e24b392f315bbc4f7c', focused: this.hasFocus }, h("label", { key: '9c1769e940c2c741325223e52d543ab74a91d041', htmlFor: this.inputId, part: "label" }, this.label, h("slot", { key: '489039b4d2e494969dd7a7ddeb558893908f5f48' }), " ", this.required ? h("span", { class: "required" }) : null), h("div", { key: 'f118f9befda791b7bb82519a8732ebda4415a731', class: "hint", part: "hint" }, h("slot", { key: '3ef61cf3c5451556e5a8b07b3a949ebce7494fda', name: "hint" })), h("div", { key: '82035bf7b1f560ea51f549d3445c53b065b54791', class: "error-message" }, h("slot", { key: '2a89e6c48ac81f80b4d16b542690b31e5bd368e6', name: "error" })), h("div", { key: '90a964e3375918eb9dcedb4bca3e9794301d7da9', class: {
                controls: true,
                "has-unit": this.hasUnit
            } }, h("input", { key: '5a2355c01c8821e91335b5ef68be1d544e0bb284',
            // @ts-expect-error Check for typing
            ref: (el) => (this.internalInput = el), type: this.type, placeholder: this.placeholder, name: this.name, id: this.inputId, disabled: this.disabled, required: this.required, readonly: this.readonly, minlength: this.minlength, maxlength: this.maxlength, max: this.max, min: this.min, step: this.step, value: this.value, spellcheck: this.spellcheck, autocapitalize: this.autocapitalize, autocomplete: this.autocomplete, autofocus: this.autofocus, inputmode: this.inputmode, onInput: () => this.handleInput(), onChange: () => this.handleChange(), onBlur: (e) => this.handleBlur(e), onFocus: () => this.handleFocus(), onKeyPress: (e) => this.handleKeyPress(e) }), h("div", { key: '9f7b6c37c80b8ff01f9ee05e327ac3890aa307e1', class: "unit" }, h("slot", { key: '3581af5962f485bcffd2c1dd7c9668876e7aeefb', name: "unit" })), !showClearable && (h("div", { key: 'cd4ac1af9bc3a909d5bd4a619ae7858dfa6adaf5', class: "icon", onClick: this.handleIconClick }, h("slot", { key: 'b4e826a8e9214ebf5e9d475c3796a30a0ccd9bf7', name: "icon" }))), showClearable ? (h("button", { type: "button", class: "clear", onClick: this.clearInput }, h("ux-icon", { name: "ux-input-clearable" }))) : null)));
    }
    static get is() { return "ux-input"; }
    static get encapsulation() { return "shadow"; }
    static get delegatesFocus() { return true; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["input.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["input.css"]
        };
    }
    static get properties() {
        return {
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Label for the input"
                },
                "attribute": "label",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"text\" | \"number\" | \"email\" | \"password\" | \"search\" | \"url\" | \"tel\" | \"hidden\"",
                    "resolved": "\"email\" | \"hidden\" | \"number\" | \"password\" | \"search\" | \"tel\" | \"text\" | \"url\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Type of input"
                },
                "attribute": "type",
                "reflect": false,
                "defaultValue": "\"text\""
            },
            "placeholder": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Placeholder for the input"
                },
                "attribute": "placeholder",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "name attribute for the input"
                },
                "attribute": "name",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "false",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Disabled attribute for the input"
                },
                "attribute": "disabled",
                "reflect": true,
                "defaultValue": "false"
            },
            "required": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "false",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "required attribute for the input"
                },
                "attribute": "required",
                "reflect": true,
                "defaultValue": "false"
            },
            "readonly": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "readonly attribute for the input"
                },
                "attribute": "readonly",
                "reflect": false,
                "defaultValue": "false"
            },
            "minlength": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "minlength attribute for the input"
                },
                "attribute": "minlength",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "maxlength": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "maxlength attribute for the input"
                },
                "attribute": "maxlength",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "min": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "min attribute for the input"
                },
                "attribute": "min",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "max": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "max attribute for the input"
                },
                "attribute": "max",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "step": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "step attribute for the input"
                },
                "attribute": "step",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Value for the input"
                },
                "attribute": "value",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "spellcheck": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "spellcheck attribute for the input"
                },
                "attribute": "spellcheck",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "autocapitalize": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "autocapitalize attribute for the input"
                },
                "attribute": "autocapitalize",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "autocomplete": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "autocomplete attribute for the input"
                },
                "attribute": "autocomplete",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "autofocus": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "autofocus attribute for the input"
                },
                "attribute": "autofocus",
                "reflect": false,
                "defaultValue": "false"
            },
            "inputmode": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"none\" | \"text\" | \"decimal\" | \"numeric\" | \"tel\" | \"search\" | \"email\" | \"url\"",
                    "resolved": "\"decimal\" | \"email\" | \"none\" | \"numeric\" | \"search\" | \"tel\" | \"text\" | \"url\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "inputmode attribute for the input"
                },
                "attribute": "inputmode",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "clearable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "adds a clear button that empties the input value"
                },
                "attribute": "clearable",
                "reflect": false,
                "defaultValue": "false"
            },
            "invalid": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "false",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "indicates if the input value is invalid"
                },
                "attribute": "invalid",
                "reflect": true,
                "defaultValue": "false"
            },
            "validity": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "ValidityState",
                    "resolved": "ValidityState",
                    "references": {
                        "ValidityState": {
                            "location": "global",
                            "id": "global::ValidityState"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "indicates the ValidityState of the input"
                },
                "defaultValue": "undefined"
            }
        };
    }
    static get states() {
        return {
            "hasFocus": {}
        };
    }
    static get events() {
        return [{
                "method": "uxInput",
                "name": "uxInput",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when a keyboard input occurred."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }, {
                "method": "uxInputChange",
                "name": "uxInputChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the value has changed ."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }, {
                "method": "uxIconClick",
                "name": "uxIconClick",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the icon slot was clicked. Use e.preventDefault() to disable default behavior (focus the input)"
                },
                "complexType": {
                    "original": "MouseEvent",
                    "resolved": "MouseEvent",
                    "references": {
                        "MouseEvent": {
                            "location": "global",
                            "id": "global::MouseEvent"
                        }
                    }
                }
            }, {
                "method": "uxClear",
                "name": "uxClear",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the clear button was clicked. Use e.preventDefault() to disable default behavior (clear and focus input)"
                },
                "complexType": {
                    "original": "MouseEvent",
                    "resolved": "MouseEvent",
                    "references": {
                        "MouseEvent": {
                            "location": "global",
                            "id": "global::MouseEvent"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "getInputElement": {
                "complexType": {
                    "signature": "() => Promise<HTMLInputElement>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLInputElement": {
                            "location": "global",
                            "id": "global::HTMLInputElement"
                        }
                    },
                    "return": "Promise<HTMLInputElement>"
                },
                "docs": {
                    "text": "Returns the native input element",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "host"; }
    static get watchers() {
        return [{
                "propName": "invalid",
                "methodName": "setValidity"
            }, {
                "propName": "required",
                "methodName": "setRequired"
            }, {
                "propName": "disabled",
                "methodName": "setDisabled"
            }];
    }
    static get attachInternalsMemberName() { return "internals"; }
}
//# sourceMappingURL=input.js.map
